import { Vue, setup } from 'vue-class-component';
import LiveSplashUploader from '../components/live-splash-uploader';
import {
  LiveChannel$Indexing,
  LiveEvent$Details,
  LiveEvent$Metadata,
  LiveProcessing$Settings,
  LiveSource,
  LiveSource$Encoding,
  LiveSource$Type,
} from '../types/Live';
import { today, nextTimeInTimes } from '@/helpers/date';
import { TimeZone, TimeZone$Labeled, PREDEFINED_ZONES } from '../types/TimeZone';
import { liveChannelsService, liveSourcesService, timeZonesService } from '../live-facade';
import prop from '@Ravnur-Inc/nanoutils/prop';
import sortBy from '@Ravnur-Inc/nanoutils/sortBy';
import flow from '@Ravnur-Inc/nanoutils/flow';
import map from '@Ravnur-Inc/nanoutils/map';
import moment from 'moment';
import momentTimezone from 'moment-timezone';
import { StandardOption } from '@/types/Component';
import { UNLIMITED_EVENT_DURATION_IN_MIN } from '@Ravnur-Inc/shared/types/Live';
import minutes2string from '@/transformers/duration';
import { Watch } from '@Ravnur-Inc/decorators';
import { toString, time } from '@/transformers/date';
import { LiveTemplate$Details } from '../types/LiveTemplate';
import { Attachment } from '@/types/Attachment';
import useSecurityStore from '@/store/security';

const m = moment();
export const DEFAULT_DURATION = 60;
const MINS_IN_ONE_DAY = 1440;

const prepareTimeZones: (arr: TimeZone[]) => TimeZone$Labeled[] = flow(
  sortBy(prop('offset')),
  map(toLabeled)
);

export const duration2option = (d: number) => ({ id: d, label: minutes2string(d) });

export default class LiveEventFormMixin extends Vue {
  declare $refs: {
    splash: LiveSplashUploader | undefined;
  };

  security = setup(() => useSecurityStore());

  id: Nullable<string> = null;
  templateName = '';
  selectedAttachment: Nullable<File | Attachment> = null;
  title = '';
  description = '';
  metadata: LiveEvent$Metadata[] = [];
  startDate: Nullable<string> = null;
  startTime: Nullable<string> = null;
  timeZoneId: Nullable<string> = null;
  channelIds: string[] = [];
  duration: number = DEFAULT_DURATION;
  splashUrl: Nullable<string> = null;
  hasUnunploadedSplash = false;
  autoStartPreview = false;
  previewAutoStartTime: Nullable<number> = null;
  autoStartLive = false;
  liveAutoStartTime: Nullable<number> = null;
  publishImmediately = false;
  languageId: Nullable<string> = null;
  liveSourceId: Nullable<string> = null;
  notConvertToVOD = false;
  channelId: Nullable<string> = null;
  loading = false;
  localAccessCodeToggle = false;
  accessCode: Nullable<string> = null;
  indexing: Nullable<LiveChannel$Indexing> = LiveChannel$Indexing.NONE;

  get today(): Date {
    return today();
  }

  get predefinedZones() {
    const zones = PREDEFINED_ZONES;
    const userTimeZone = momentTimezone.tz.guess();

    if (!zones.includes(userTimeZone)) {
      zones.push(userTimeZone);
    }

    return zones;
  }

  get timeZones(): TimeZone$Labeled[] {
    const filtered = timeZonesService.list.filter(({ id }) => this.predefinedZones.includes(id));

    return prepareTimeZones(filtered);
  }

  get processingSettings() {
    return {
      publishImmediately: this.publishImmediately,
      indexing: this.indexing,
      notConvertToVOD: this.notConvertToVOD,
      languageId: this.languageId,
    };
  }

  get durations(): Array<StandardOption<number>> {
    const isBasicLiveSource = this.liveSource?.encoding === LiveSource$Encoding.PASSTHROUGH_BASIC;
    const durations = isBasicLiveSource
      ? this.prepareDurations(480).map(duration2option)
      : this.prepareDurations().map(duration2option);

    const unlimited = {
      id: UNLIMITED_EVENT_DURATION_IN_MIN,
      label: this.$t('live-events', 'unlimited_duration'),
    };

    return isBasicLiveSource ? durations : [unlimited, ...durations];
  }

  get liveSource(): Nullable<LiveSource> {
    const { liveSourceId } = this;
    const { cache } = liveSourcesService;
    return liveSourceId ? cache[liveSourceId] : null;
  }

  get isMUXSource() {
    return this.liveSource?.type === LiveSource$Type.MUX;
  }

  get isRMSLiveSource() {
    return this.liveSource?.type === LiveSource$Type.RMS;
  }

  get isDVRSupported() {
    return this.liveSource?.isDvr;
  }

  get liveAutoStartOptions(): Array<StandardOption<number>> {
    return this.prepareDurations(60, 5, true).map(duration2option);
  }

  get previewAutoStartOptions(): Array<StandardOption<number>> {
    return this.prepareDurations(60, 5).map(duration2option);
  }

  @Watch('timeZones', { immediate: true })
  protected handleTimeZonesChanged() {
    const { timeZones, timeZoneId } = this;
    if (!timeZones.length || !!timeZoneId) {
      return;
    }

    this.timeZoneId = momentTimezone.tz.guess();

    if (this.timeZoneId) {
      return;
    }

    const now = new Date();
    const tzOffset = -now.getTimezoneOffset();
    const [tz] = timeZones.filter(({ offset }) => offset === tzOffset);

    this.timeZoneId = tz.id;
  }

  @Watch('autoStartPreview')
  protected handleAutoStartPreviewChanged(autoStartPreview: boolean) {
    if (!autoStartPreview) {
      this.previewAutoStartTime = null;

      return;
    }

    if (!this.previewAutoStartTime) {
      this.previewAutoStartTime = this.previewAutoStartOptions[0].id;
    }
  }

  @Watch('autoStartLive')
  protected handleAutoStartLiveChanged(autoStartLive: boolean) {
    if (!autoStartLive) {
      this.liveAutoStartTime = null;

      return;
    }

    if (this.liveAutoStartTime) {
      return;
    }

    this.liveAutoStartTime = this.liveAutoStartOptions[0].id;
  }

  changeProcessingSettings(settings: LiveProcessing$Settings) {
    this.publishImmediately = settings.publishImmediately;
    this.indexing = settings.indexing;
    this.languageId = settings.languageId;
    this.notConvertToVOD = !!settings.notConvertToVOD;
  }

  handleSplashChanged(uploadNew: boolean) {
    if (!uploadNew) {
      this.hasUnunploadedSplash = true;
    }

    this.hasUnunploadedSplash = true;
  }

  setStartTime(startTime: string) {
    this.startTime = startTime;
  }

  generateForm() {
    const form: Partial<LiveEvent$Details> = {
      title: this.title,
      description: this.description,
      metadata: this.metadata,
      duration: this.duration,
      autoStartPreview: this.autoStartPreview,
      previewAutoStartTime: this.previewAutoStartTime,
      autoStartLive: this.autoStartLive,
      liveAutoStartTime: this.liveAutoStartTime,
      publishImmediately: this.publishImmediately,
      languageId: this.languageId,
      indexing: this.indexing,
      accessCode: this.localAccessCodeToggle ? this.accessCode : null,
      splashUrl: this.splashUrl,
    };

    if (this.liveSourceId) {
      form.liveSourceId = this.liveSourceId;
    }

    if (this.isRMSLiveSource || this.isMUXSource) {
      form.notConvertToVOD = this.notConvertToVOD;
    }

    if (this.startDate) {
      form.startDate = toString(this.startDate as any, false);
    }

    if (this.startTime) {
      form.startTime = this.startTime;
    }

    if (this.timeZoneId) {
      form.timeZoneId = this.timeZoneId;
    }

    if (this.hasUnunploadedSplash) {
      form.ts = `${Date.now()}`;
    }

    if (this.selectedAttachment && 'kind' in this.selectedAttachment) {
      form.attachments = [this.selectedAttachment];
    }

    return form;
  }

  generateTemplateForm() {
    const form: Partial<LiveTemplate$Details> = {
      templateName: this.templateName,
      title: this.title,
      description: this.description,
      duration: this.duration,
      publishImmediately: this.publishImmediately,
      indexing: this.indexing,
      liveSourceId: this.liveSourceId,
      channelIds: this.channelId ? [this.channelId] : [],
      languageId: this.languageId,
      metadata: this.metadata,
      autoStartLive: this.autoStartLive,
      autoStartPreview: this.autoStartPreview,
    };

    if (this.previewAutoStartTime !== null) {
      form.previewAutoStartTime = this.previewAutoStartTime;
    }

    if (this.autoStartLive && this.liveAutoStartTime !== null) {
      form.liveAutoStartTime = this.liveAutoStartTime;
    }

    if (this.isRMSLiveSource || this.isMUXSource) {
      form.notConvertToVOD = this.notConvertToVOD;
    }

    if (this.timeZoneId) {
      form.timeZoneId = this.timeZoneId;
    }

    return form;
  }

  prepareDurations(limit: number = MINS_IN_ONE_DAY, step = 15, startsWithZero = false): number[] {
    const res = startsWithZero ? [0] : [];
    let d = 0;

    while (d < limit) {
      d += d < 180 ? step : 60;
      res.push(d);
    }

    return res;
  }

  checkVideoTranscriptionsByDefault() {
    if (this.security.isVideoTranscriptionsByDefault) {
      this.indexing = LiveChannel$Indexing.AUDIO;
    }
  }

  created() {
    this.startDate = toString(this.today);
    this.startTime = time(nextTimeInTimes());

    this.checkVideoTranscriptionsByDefault();

    timeZonesService.load();
    liveSourcesService.load();
    liveChannelsService.load();
  }
}

function toLabeled(tz: TimeZone): TimeZone$Labeled {
  return { ...tz, label: detectLabel(tz) };
}

function detectLabel({ id, offset }: TimeZone): string {
  m.utcOffset(offset);
  const prefix = `(GMT ${m.format('Z')})`;
  const suffix = (id.split('/').pop() || '').split('_').join(' ');
  return `${prefix} ${suffix}`;
}
